import { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useApiError } from "../../../../hooks/useApiError";
import { recipeService } from "../../../../services/recipe-service";

type Props = {
  recipeId: number | null;
  isEdit: boolean;
};

export default function RecipeImageSection({ recipeId, isEdit }: Props) {
  const { handleError, ErrorAlert, clearError } = useApiError();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);

  const localPreview = useMemo(() => {
    if (!selectedFile) return "";
    return URL.createObjectURL(selectedFile);
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  async function loadExistingPreview() {
    if (!isEdit || !recipeId) return;

    setLoadingPreview(true);
    try {
      const url = await recipeService.getImageUrl(recipeId);
      setPreviewUrl(url || "");
    } catch (err) {
      handleError(err, "Failed to load recipe image.");
    } finally {
      setLoadingPreview(false);
    }
  }

  useEffect(() => {
    loadExistingPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, recipeId]);

  async function handleUpload() {
    clearError();

    if (!isEdit || !recipeId) {
      handleError(null, "Save the recipe first, then upload an image.");
      return;
    }

    if (!selectedFile) {
      handleError(null, "Please choose an image to upload.");
      return;
    }

    setUploading(true);
    try {
      await recipeService.uploadImage(recipeId, selectedFile);
      setSelectedFile(null);

      // reload SAS url after upload
      await loadExistingPreview();
    } catch (err) {
      handleError(err, "Failed to upload image.");
    } finally {
      setUploading(false);
    }
  }

  const displayImage = localPreview || previewUrl;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Recipe image
      </Typography>

      <ErrorAlert />

      {!isEdit ? (
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
          Create the recipe first, then you can upload an image.
        </Typography>
      ) : null}

      <Box
        sx={{
          border: "1px dashed rgba(0,0,0,0.2)",
          borderRadius: 2,
          p: 2,
          mb: 2,
          minHeight: 160,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {loadingPreview ? (
          <CircularProgress />
        ) : displayImage ? (
          <img
            src={displayImage}
            alt="Recipe"
            style={{ width: "100%", height: "auto", display: "block", borderRadius: 8 }}
          />
        ) : (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No image uploaded yet.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="outlined" component="label" disabled={uploading || !isEdit}>
          Choose file
          <input
            type="file"
            hidden
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => {
              clearError();
              const f = e.target.files?.[0] ?? null;
              setSelectedFile(f);
            }}
          />
        </Button>

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={uploading || !isEdit || !selectedFile}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>

        {selectedFile ? (
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {selectedFile.name}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
